import { useCallback, useEffect, useMemo, useState } from "react";
import { useViimeisin } from "./tuoreus";
import { naytaPvm } from "./pvm";
import { api, ApiError, type Kayttaja, type Laippaliitos } from "./api";
import { t, virheTeksti, type KaannosAvain } from "./kieli";
import Laippakiristys from "./Laippakiristys";

/* ═══════════════════════════════════════════════════════════
   Laippaliitokset kenttätilille (T636, Toni 25.10.2026 klo 13.55 ja 19.00).

   Oma sisäänkäynti ilman hitsauslokia: linjasivu on hitsauslokin näkymä, ja sen reitit ovat hitsaustyön työlajin
   takana, joten pelkkä laippa-asentaja ei päässyt kiristämään. Tämä näkymä lukee vain moduulin «laippaliitokset»
   reittejä — lista omien työmaiden liitoksista (rajaus palvelimella), haku, ja liitokselta kiristykset ja
   puhelinlomake (Laippakiristys.tsx). Valikko näyttää kohdan moduulin ja työlajin «laippatyö» ehdolla.
   ═══════════════════════════════════════════════════════════ */

const KASITTELIJAT = ["paakayttaja", "tyonjohtaja", "hitsauksen_tyonjohtaja", "tarkastaja"];

export default function LaippaKentta({ kayttaja }: { kayttaja: Kayttaja }) {
  const [lista, setLista] = useState<Laippaliitos[] | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [haku, setHaku] = useState("");
  const [auki, setAuki] = useState<number | null>(null);

  const uusi = useViimeisin();
  const lataa = useCallback(async () => {
    const tuore = uusi();
    try {
      const l = await api.laippaliitokset({});
      if (!tuore()) return;
      setLista(l); setVirhe(null);
    } catch (e) {
      if (!tuore()) return;
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("muu.laippa.hakuvirhe"));
      setLista([]);
    }
  }, [uusi]);
  useEffect(() => { void lataa(); }, [lataa]);

  const nakyvat = useMemo(() => {
    const h = haku.trim().toLowerCase();
    return (lista ?? []).filter((l) => !h || [l.tunnus, l.linja_tunnus, l.laite_tunnus, l.kohde_tunnus]
      .some((x) => (x ?? "").toLowerCase().includes(h)));
  }, [lista, haku]);

  const saaKasitella = kayttaja.roolit.some((r) => KASITTELIJAT.includes(r));
  if (lista === null) return <main><p className="dim">{t("muu.laippa.ladataan")}</p></main>;

  return (
    <main data-laippa-kentta>
      <h1>{t("muu.laippa.otsikko")}</h1>
      <p className="dim">{t("lk.nakyma.selite")}</p>
      {virhe && <p className="huom" role="alert">{virhe}</p>}
      <label style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 420 }}>{t("lk.haku")}
        <input style={{ minHeight: 44, fontSize: 16 }} value={haku} data-lk-haku onChange={(e) => setHaku(e.target.value)} /></label>
      {nakyvat.length === 0 ? <p className="dim" data-rivit="0">{t("lk.ei_liitoksia")}</p> : (
        <ul style={{ listStyle: "none", padding: 0, margin: "12px 0" }} data-rivit={nakyvat.length}>
          {nakyvat.map((l) => (
            <li key={l.id} style={{ borderTop: "1px solid var(--viiva)", padding: "8px 0" }}>
              <button className="nappi nappi--kevyt" style={{ minHeight: 44, width: "100%", textAlign: "left" }}
                      data-lk-liitos={l.id} aria-expanded={auki === l.id} onClick={() => setAuki(auki === l.id ? null : l.id)}>
                <b className="mono">{l.tunnus}</b> · {l.linja_tunnus ?? l.laite_tunnus ?? "–"}
                {l.kohde_tunnus ? <span className="dim"> · {l.kohde_tunnus}</span> : null}
                {l.koko_dn != null ? <span className="dim"> · DN {l.koko_dn}</span> : null}
                <span className="dim"> · {t("lk.kiristykset")} ({l.kiristyksia ?? 0})
                  {l.viimeisin_tila ? ` · ${t(`lk.tila.${l.viimeisin_tila}` as KaannosAvain)} ${naytaPvm(l.viimeisin_paiva)}` : ""}</span>
              </button>
              {auki === l.id && (
                <Laippakiristys liitosId={l.id} saaKiristaa={kayttaja.laippatyo === true} saaKasitella={saaKasitella}
                                onMuutos={() => void lataa()} />
              )}
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
